import { TrashIcon } from '@calizahq/react-components-v2'
import React, { useState } from 'react'

import { Card, LineItem, Modal, Tag } from '@/components'
import { useAlertContext } from '@/context'
import { PropertyQueries } from '@/graphql/queries/property.queries'
import { useReservationHome, useToggle } from '@/hooks'
import { useGraphql } from '@/hooks/useGraphql'
import { recordBlockReasons } from '@/types/Homes'
import { ReservationProperty } from '@/types/Property'
import { ReservationDetail } from '@/types/Reservation'

import { DeleteBlockReasonForm } from './DeleteBlockReasonForm/DeleteBlockReasonForm'
import { QuickActionFob } from './QuickActionFob'

enum BlockRecordType {
  HOME = 'Home',
  PROPERTY = 'Property',
}

interface DeleteRecordType {
  reason: string
  recordId: number
  type: BlockRecordType
}

export function StatusView({ reservationData }: { reservationData: ReservationDetail }) {
  const { data: propertyData, refetch: refetchProperty } = useGraphql<{ property: ReservationProperty }>(
    PropertyQueries.QUERY_KEYS.getReservationProperty,
    PropertyQueries.getReservationProperty(),
    { id: reservationData.home.property.id },
    {
      enabled: !!reservationData,
    },
  )

  const { data: homeData, refetch: refetchHome } = useReservationHome({ homeId: reservationData?.home?.id })

  const { setAlert } = useAlertContext()

  const {
    propertyOnlinePortal,
    propertyPortalPassword,
    propertyPortalUsername,
    occupied = false,
  } = homeData?.home || {}
  const { moveInMethod } = reservationData
  const recordBlockReasons = propertyData?.property?.recordBlockReasons || []
  const homeRecordBlockReasons = homeData?.home?.recordBlockReasons || []
  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const [deleteRecord, setDeleteRecord] = useState<DeleteRecordType>()

  const isPropertyBlocked = recordBlockReasons?.length !== 0
  const isHomeBlocked = homeRecordBlockReasons?.length !== 0
  const isFlightX = reservationData.flightxInProgress
  const isLexit = reservationData.lexit

  const handleClick = (message: string, value?: string) => {
    if (message.length > 0 && value) {
      navigator.clipboard.writeText(value)
      setAlert(message, 'success')
    }
  }
  const handleDeleteRecord = (recordId: number, reason: string, type: BlockRecordType) => {
    if (recordId) {
      setDeleteRecord({ recordId, reason, type })
      modalToggle()
    }
  }

  const handleCloseModal = () => {
    setDeleteRecord(undefined)
    modalToggle()
  }

  const handleOnDelete = () => {
    if (deleteRecord) {
      if (deleteRecord.type === BlockRecordType.HOME) {
        refetchHome()
      } else {
        refetchProperty()
      }
    }
    handleCloseModal()
  }

  return (
    <>
      <div>
        <div className="flex flex-wrap">
          <Tag className="mb-4 mr-4" variant={occupied ? 'error' : 'success'}>
            {occupied ? 'Occupied' : 'Vacant'}
          </Tag>
          <Tag className="mb-4 mr-4" variant={'success'}>
            {moveInMethod == 'self' ? 'Self Check-in' : 'Host Check-in'}
          </Tag>
          <Tag className="mb-4 mr-4 capitalize" variant={'info'}>
            {reservationData.status}
          </Tag>

          {isPropertyBlocked && (
            <Tag className="mb-4 mr-4" variant={`error`}>
              Property Blocked
            </Tag>
          )}
          {isHomeBlocked && (
            <Tag className="mb-4 mr-4" variant={`error`}>
              Home Blocked
            </Tag>
          )}
          {isFlightX && (
            <Tag className="mb-4 mr-4" variant={`error`}>
              Flight X
            </Tag>
          )}
          {isLexit && (
            <Tag className="mb-4 mr-4" variant={`error`}>
              Lexit
            </Tag>
          )}
          {reservationData.includeDamageWaiverFee && (
            <Tag className="mb-4 mr-4" variant={`success`}>
              Damage Waiver
            </Tag>
          )}
        </div>

        {propertyOnlinePortal ? (
          <Card cardClassName="mb-4" headerText="Property Portal" minimizable={true}>
            <div className="flex w-full flex-col px-4">
              <button onDoubleClick={() => handleClick('Username copied!', propertyPortalUsername)}>
                <LineItem label="Username" value={propertyPortalUsername} />
              </button>
              <button onDoubleClick={() => handleClick('Password copied!', propertyPortalPassword)}>
                <LineItem label="Password" last={true} value={propertyPortalPassword} />
              </button>
              <a
                className=" mb-2 w-full rounded-lg bg-action-primary py-2 text-center text-bg-primary"
                href={propertyOnlinePortal}
                rel="noreferrer"
                target="_blank"
              >
                Property Portal Link
              </a>
            </div>
          </Card>
        ) : (
          <p className="mb-4 rounded-lg bg-action-secondary p-4"> Property Portal Information Missing </p>
        )}
        {recordBlockReasons?.length > 0 && (
          <RecordBlockReasonCard
            cardHeader="Property Block Reasons"
            deleteRecord={(recordId, reason) => handleDeleteRecord(recordId, reason, BlockRecordType.PROPERTY)}
            recordBlockReasons={recordBlockReasons}
          />
        )}
        {homeRecordBlockReasons?.length > 0 && (
          <RecordBlockReasonCard
            cardHeader="Home Block Reasons"
            deleteRecord={(recordId, reason) => handleDeleteRecord(recordId, reason, BlockRecordType.HOME)}
            recordBlockReasons={homeRecordBlockReasons}
          />
        )}
      </div>
      <QuickActionFob
        homeId={reservationData.home.id}
        propertyId={reservationData.home.property.id}
        refetchHome={refetchHome}
        refetchProperty={refetchProperty}
        reservationId={reservationData.id}
      />
      <Modal headerText="Delete Block Reason" isCloseShown={false} isShown={modalShown} toggle={modalToggle}>
        <div className="flex p-6">
          {deleteRecord && (
            <DeleteBlockReasonForm
              handleOnClose={handleCloseModal}
              handleOnDelete={handleOnDelete}
              reason={deleteRecord.reason}
              recordId={deleteRecord.recordId}
            />
          )}
        </div>
      </Modal>
    </>
  )
}

type RecordBlockReasonCardProps = {
  cardHeader: string
  deleteRecord: (recordId: number, reason: string) => void
  recordBlockReasons: recordBlockReasons[]
}

const RecordBlockReasonCard = ({ recordBlockReasons, cardHeader, deleteRecord }: RecordBlockReasonCardProps) => {
  return (
    <Card cardClassName="mb-4" headerText={cardHeader}>
      <div className="flex w-full flex-col px-4">
        {recordBlockReasons.map(({ blockReason, id }, index) => {
          if (blockReason && blockReason.description) {
            return (
              <div className={`flex ${index > 0 ? 'border-t border-secondary' : ''}`} key={index}>
                <p className={`w-full py-4 text-sm leading-140 `}>{blockReason.description}</p>
                <TrashIcon
                  className="w-5 cursor-pointer icon-tertiary"
                  onClick={() => deleteRecord(id, blockReason.description || '')}
                />
              </div>
            )
          }
        })}
      </div>
    </Card>
  )
}
